/**
 * The SEQUENCE canvas: the cycle as a row of numbered columns, from `Builder planu sekwencja`.
 *
 * The mock's row is `flex; align-items: stretch; gap: 10px; padding: 14px 20px 16px; overflow-x:
 * auto` — the week programator's grid with different columns in it. A training column is
 * `1 1 252px` capped at 496px, a gap column a fixed 84px (no break) or 108px (a break), and two
 * fixed 120px tiles close the run: the dashed "+ Dzień treningowy" and the static loop mark.
 *
 * ── Every column carries the same head, and the pane is what draws it ────────────────────────
 * Two centred lines, `padding: 0 2px`, clipping rather than wrapping: "DZIEŃ 3" over "~64 min" on a
 * training, "DNI 4–5" alone over a break, and two empty spans over each tail tile so the three
 * kinds of column stand on one baseline. Drawing it here rather than inside each column is what
 * stops the same two spans and the same two classes existing in three stylesheets.
 *
 * **The heads stay exposed to assistive technology.** After a break the printed day number diverges
 * from the training's POSITION — "DZIEŃ 3" can be the second training — and the two facts are both
 * real and both reachable: the head says where the day falls in the cycle, and the move machinery
 * says "Pozycja 2 z 3". Hiding the head would remove the first, which is the one the mock draws.
 *
 * ── It is the phase tabs' panel, exactly as the week pane is ─────────────────────────────────
 * `role="tabpanel"`, labelled by the selected tab, `tabIndex={0}` because it scrolls sideways.
 *
 * Its `h2` was the visible "TAŚMA CYKLU" band the 04.08 canon drew and the mock removes. The BAND
 * goes; the heading does not. The only other headings on this screen are the top bar's `h1` and each
 * card's `h3`, so without one the outline jumps H1 → H3 and a reader navigating by heading is told
 * the cards belong to a section that does not exist. It is visually hidden, exactly as `WeekPane`
 * hides its twin.
 *
 * ── The ROW is unconditional; only its middle has an empty state ─────────────────────────────
 * A phase with no training draws the empty state where the training and gap columns would stand,
 * and the two tail tiles stay beside it. They were briefly inside the else-branch, which made an
 * empty self-paced phase a dead end: the canvas held zero buttons while the top bar told the trainer
 * to add an exercise, and the only exits were undo, deleting the phase, or a reload. The add tile IS
 * the way out of the empty state, so it cannot be a thing the empty state removes.
 *
 * ── Every hidden span here is `shared/ui/visuallyHidden` ─────────────────────────────────────
 * There is one visually-hidden treatment in this app and this file used to carry a second, local
 * one. The shared class is `position: fixed` for a measured reason — an absolutely positioned box
 * contributes to the DOCUMENT's scrollable overflow even at its own static position, which on a
 * fourteen-column row put `documentElement.scrollWidth` thousands of pixels past the viewport. The
 * local copy was `absolute` and was held in check only by `.pane`'s `position: relative`; using the
 * shared one removes the dependency rather than relying on it.
 *
 * ── Gap columns are not drop targets ─────────────────────────────────────────────────────────
 * See `RestColumn`'s header: a break is the tail of the training before it, it has no slot token,
 * and `moveDayToPosition` has no coordinate that names it. Dropping "between trainings 2 and 3" is
 * already dropping ON training 3. The keyboard path's stops are the trainings alone, for the same
 * reason — `positionTargets` builds one entry per training, so the arrows cannot step onto a
 * position that could not receive a day.
 *
 * ── The loop tile is static, and stays static ────────────────────────────────────────────────
 * No handler, no `role="button"`, no tab stop. The mock draws a plain panel with no hover, and a
 * decorative tile that looks pressable is a defect of its own. Both of its drawn lines are
 * `aria-hidden` under one sr-only sentence, because read as they are drawn they run together into
 * "Cykl zapętla się wraca do dnia 1".
 *
 * ── The edge fades are gone, the sentence they carried is not ────────────────────────────────
 * The 04.08 canvas drew a gradient over each clipped edge. The mock draws none, and nothing the mock
 * does not draw may be visible (owner, 8 September 2026), so both pseudo-elements and the
 * non-scrolling frame that existed to hold them are removed. `useOverflowEdge` stays: it is what
 * puts the same fact into WORDS on the region, through `aria-describedby`, and that half was never
 * the mock's to remove.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { useSwapReplay } from '~/shared/lib/useSwapReplay';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import swap from '~/shared/ui/swapReplay.module.css';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';
import { useAutoScroll } from '~/shared/ui/dnd/useAutoScroll';

import { useOverflowEdge } from '~/shared/lib/useOverflowEdge';
import type { OverlayRoom } from '../model/revealDay';
import type { CycleColumnView, TapeViewModel } from '../model/tapeViewModel';

import { CycleColumn } from './CycleColumn';
import { RestColumn } from './RestColumn';
import { RENAME_HINT_ID, type DayCardActions, type SessionMoveProps } from './SessionCard';
import styles from './TapePane.module.css';

/** The id of the ONE keyboard instruction on this canvas — see `SessionCard`'s `RENAME_HINT_ID`. */
export const TAPE_MOVE_HINT_ID = 'plan-builder-tape-move-hint';

/**
 * One column's head: where it falls in the cycle, and how long the training on it runs.
 *
 * Both spans are emitted in every state, empty ones included. That is the mock's own `tail()`, and
 * it is what keeps a gap column's tile and a tail tile starting at the same y as the card beside
 * them — an empty span is zero pixels tall, so a column with no second line simply starts higher,
 * which is what the mock renders.
 */
function ColumnHead({ head, sub }: { readonly head: string | null; readonly sub: string | null }) {
  return (
    <div className={styles.head}>
      <span className={styles.cap}>{head ?? ''}</span>
      <span className={styles.sub}>{sub ?? ''}</span>
    </div>
  );
}

interface TapePaneProps {
  readonly id: string;
  readonly tape: TapeViewModel;
  /** The id of the phase tab this panel belongs to. */
  readonly labelledBy: string;
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  readonly onProgramDay: ((slotToken: string) => void) | null;
  /** Write a day's gap. `null` when the plan cannot be edited. */
  readonly onChangeRest: ((daySlotToken: string, days: number) => void) | null;
  /** Append a training to the phase and open the dock on it. `null` when the plan cannot be edited. */
  readonly onAddDay: (() => void) | null;
  readonly returnFocusToken: string | null;
  readonly move: SessionMoveProps | null;
  /** Duplicate and delete, forwarded to every card. `null` on a plan that cannot be edited. */
  readonly actions: DayCardActions | null;
  readonly announcement: string;
  /** Which POSITION a keyboard lift is pointed at, so that column lights up. `null` when none is. */
  readonly pendingTarget: number | null;
  /**
   * The phase the canvas is showing — the RESOLVED index, not the raw `?phase=`.
   *
   * Its only job here is to replay the mock's `swapA` / `swapB` fade when the trainer steps between
   * phases. The columns themselves come from `tape`; this is the signal that they came from
   * somewhere else.
   */
  readonly phaseIndex: number;
  /** The slot token of the day whose panel is open, or `null` — the accent edge and ember ring. */
  readonly openDaySlotToken: string | null;
  /**
   * Whether the pane's right-hand end is covered by the day panel.
   *
   * The one thing the pane does with it is grow trailing scroll room the width of the panel, out of
   * flow, so a column standing under it can be scrolled clear. The pane still does not learn what a
   * panel IS, only that something that wide covers its end — the same seam the week pane takes.
   */
  readonly panelRoom: OverlayRoom;
}

export function TapePane({
  id,
  tape,
  labelledBy,
  onRenameSession,
  onProgramDay,
  onChangeRest,
  onAddDay,
  returnFocusToken,
  actions,
  move,
  announcement,
  pendingTarget,
  phaseIndex,
  openDaySlotToken,
  panelRoom,
}: TapePaneProps) {
  const { t } = useTranslation();
  const headingId = `${id}-heading`;
  const scrollHintId = useId();
  const addReasonId = useId();

  // The pane is the scroller, so it is what a drag near either edge has to scroll AND what has to
  // say when there is more of it than fits. Both hooks take the SAME ref: one element scrolls, and
  // giving the measurement a ref of its own would leave the sentence describing nothing.
  //
  // Horizontal only: a column is as tall as the row and nothing is ever hidden above or below it.
  const { ref: scrollerRef, hasMore, hasPrevious } = useOverflowEdge<HTMLDivElement>('inline');
  useAutoScroll(scrollerRef, 'horizontal');

  /**
   * The parity that replays the fade when the trainer steps to another phase, and the `null` that
   * keeps a cold entry still. Both rules, and the reason there are two keyframes for one animation,
   * live in `useSwapReplay` and `shared/ui/swapReplay.module.css`.
   */
  const replay = useSwapReplay(phaseIndex, useScreenMotionPreference());
  const swapClass = replay === null ? '' : String(replay === 'a' ? swap.swapA : swap.swapB);

  const addReason = tape.add.disabledReason;

  return (
    <div
      ref={scrollerRef}
      className={styles.pane}
      id={id}
      /* This box is the horizontal scrollport, and `useRevealOpenDay` has to find it from
       * `TapeCanvas` — where the ref above is not available and where a third hook holding it
       * would have to be merged with the two that already do. Canvas-neutral, because the same
       * hook measures the week's pane through the same attribute. */
      data-canvas-scroller=""
      role="tabpanel"
      aria-labelledby={labelledBy}
      // The scroll is a sighted cue and nothing else, so the same fact goes into words on the
      // region it describes — present only while something is actually hidden.
      aria-describedby={hasMore || hasPrevious ? scrollHintId : undefined}
      tabIndex={0}
    >
      <h2 id={headingId} className={visuallyHidden.srOnly}>
        {tape.title}
      </h2>
      {/* Always mounted, empty when there is nothing to say — a region inserted together with its
        * text is not reliably announced. It sits inside the pane so it moves with what it
        * describes. */}
      <LiveRegionAnnouncer message={announcement} />
      {/* The sequence canvas's OWN wording. The week's hint says the arrows move "między dniami",
        * which is wrong twice here: they step between TRAININGS, and they skip every gap column —
        * which this canvas also calls a day, on its head. Positions in the cycle is what they walk.
        * A separate key rather than a reworded shared one, because the week's sentence is right
        * about the week. */}
      {move !== null && (
        <p id={TAPE_MOVE_HINT_ID} className={visuallyHidden.srOnly}>
          {t('web:planBuilder.dnd.dayGripHintPosition')}
        </p>
      )}
      {onRenameSession !== null && (
        <p id={RENAME_HINT_ID} className={visuallyHidden.srOnly}>
          {t('web:planBuilder.renameHint')}
        </p>
      )}

      <div
        className={`${String(styles.cycle)} ${swapClass}`}
        role="list"
        aria-labelledby={headingId}
        data-panel-room={panelRoom ?? undefined}
      >
        {/* A phase that programmes nothing. It replaces the training and gap columns and NOTHING
          * else — the tail tiles below stay in the row, because "+ Dzień treningowy" is the only
          * way back out of this state and moving it inside the else-branch turned an empty phase
          * into a dead end (the trainer's only exits were undo, deleting the phase, or a reload).
          * A drawing and a tone rather than a bare sentence, which is the loading policy's own
          * rule and the pair `SessionCard` already draws for a day with no exercises. */}
        {tape.columns.length === 0 ? (
          <div className={styles.emptyColumn} role="listitem">
            <EmptyState
              className={styles.empty}
              mark={<DumbbellMarkIcon />}
              tone="action"
              title={tape.emptyLabel}
            />
          </div>
        ) : (
          tape.columns.map((column: CycleColumnView) => (
            <div
              key={column.key}
              className={styles.column}
              data-kind={column.kind}
              data-gap={column.kind === 'rest' && column.rest.isZero ? 'zero' : undefined}
              role="listitem"
              // The address `useRevealOpenDay` measures a column by — a slot token rather than a
              // position, so a day moved while its panel is open keeps being the one revealed.
              data-day-slot={column.kind === 'day' ? column.day.slotToken : undefined}
            >
              <ColumnHead head={column.head} sub={column.kind === 'day' ? column.sub : null} />
              {column.kind === 'day' ? (
                <CycleColumn
                  day={column.day}
                  // The column's position in the cycle is its own index among the move's stops,
                  // which the view model already assigned — counted here with a cursor it would be
                  // a second answer to a question the model has already answered.
                  index={column.day.move.index}
                  onRenameSession={onRenameSession}
                  onProgramDay={onProgramDay}
                  returnFocusToken={returnFocusToken}
                  move={move}
                  actions={actions}
                  isPendingTarget={pendingTarget === column.day.move.index}
                  isSelected={openDaySlotToken !== null && column.day.slotToken === openDaySlotToken}
                />
              ) : (
                <RestColumn rest={column.rest} onChange={onChangeRest} />
              )}
            </div>
          ))
        )}

        {onAddDay !== null && (
          <div className={styles.column} data-kind="tail" role="listitem">
            <ColumnHead head={null} sub={null} />
            <button
              type="button"
              className={styles.add}
              // The fuller name, which CONTAINS the two words the tile prints (SC 2.5.3).
              aria-label={tape.add.label}
              aria-disabled={addReason === null ? undefined : 'true'}
              // The refusal is DESCRIBED, never a child of the button: as a child it is
              // concatenated into the accessible name, so the control announced itself as its own
              // verb followed by the paragraph explaining why it cannot act.
              aria-describedby={addReason === null ? undefined : addReasonId}
              onClick={() => {
                if (addReason === null) onAddDay();
              }}
            >
              <span className={styles.addGlyph} aria-hidden="true">
                +
              </span>
              <span className={styles.tileLabel}>{tape.add.tileLabel}</span>
            </button>
            {/* On screen rather than only on `title`: this is the one place the phase's day cap is
              * ever stated, and a `title` reaches neither a touch user nor a keyboard user who
              * never hovers. */}
            {addReason !== null && (
              <p id={addReasonId} className={styles.addReason}>
                {addReason}
              </p>
            )}
          </div>
        )}

        <div className={styles.column} data-kind="tail" role="listitem">
          <ColumnHead head={null} sub={null} />
          {/* Static: no handler, no role, no tab stop — see the header. */}
          <div className={styles.loop}>
            <span className={styles.loopGlyph} aria-hidden="true">
              ↻
            </span>
            <span className={styles.tileLabel} aria-hidden="true">
              {tape.loop.title}
            </span>
            <span className={styles.loopHint} aria-hidden="true">
              {tape.loop.hint}
            </span>
            {/* The sentence, as CONTENT rather than as `aria-describedby`: nothing here is
              * focusable and nothing has a role, so a description would never be announced. In
              * document order it is read exactly where the tile stands. */}
            <span className={visuallyHidden.srOnly}>{tape.loop.description}</span>
          </div>
        </div>
      </div>

      {/* What the clipped edges say, in words, for a reader who cannot see them. */}
      {(hasMore || hasPrevious) && (
        <p id={scrollHintId} className={visuallyHidden.srOnly}>
          {t('web:planBuilder.tape.scrollHint')}
        </p>
      )}
    </div>
  );
}
